/**
 * Tiny WebAudio synth for prototype SFX until real sounds arrive (docs/audio/audio-list.md).
 * Soft sine/triangle blips only — calm, never harsh. Audio starts only after a user gesture (plan §4.4).
 */
import { audioContext, onAudioReady } from './audioContext';

export type SfxName =
  'pick' | 'drop' | 'rightSpot' | 'invalid' | 'open' | 'tap' | 'coin' | 'bell' | 'shutter';

export interface SfxPort {
  play(name: SfxName): void;
  setVolume(volume: number): void;
  setMuted(muted: boolean): void;
}

type Note = { f: number; at: number; dur: number; type?: OscillatorType; gain?: number };

const PATCHES: Record<SfxName, Note[]> = {
  pick: [{ f: 660, at: 0, dur: 0.06, type: 'triangle', gain: 0.5 }],
  drop: [
    { f: 220, at: 0, dur: 0.09, type: 'sine', gain: 0.9 },
    { f: 330, at: 0.02, dur: 0.07, type: 'triangle', gain: 0.25 },
  ],
  rightSpot: [
    { f: 784, at: 0, dur: 0.18, type: 'sine', gain: 0.55 },
    { f: 1175, at: 0.09, dur: 0.28, type: 'sine', gain: 0.45 },
    { f: 1568, at: 0.17, dur: 0.32, type: 'triangle', gain: 0.18 },
  ],
  invalid: [{ f: 196, at: 0, dur: 0.12, type: 'sine', gain: 0.45 }],
  open: [
    { f: 392, at: 0, dur: 0.08, type: 'triangle', gain: 0.4 },
    { f: 523, at: 0.07, dur: 0.12, type: 'triangle', gain: 0.35 },
  ],
  tap: [{ f: 880, at: 0, dur: 0.04, type: 'sine', gain: 0.25 }],
  coin: [
    { f: 1319, at: 0, dur: 0.07, type: 'triangle', gain: 0.35 },
    { f: 1760, at: 0.06, dur: 0.16, type: 'sine', gain: 0.35 },
  ],
  // Shop door bell when a customer walks in.
  bell: [
    { f: 1047, at: 0, dur: 0.35, type: 'sine', gain: 0.22 },
    { f: 1397, at: 0.12, dur: 0.4, type: 'sine', gain: 0.16 },
  ],
  // Photo mode: a soft two-step click.
  shutter: [
    { f: 1800, at: 0, dur: 0.03, type: 'square', gain: 0.12 },
    { f: 900, at: 0.05, dur: 0.05, type: 'triangle', gain: 0.25 },
  ],
};

export function createSynthSfx(): SfxPort {
  let master: GainNode | undefined;
  let volume = 0.5;
  let muted = false;

  onAudioReady((ctx) => {
    master = ctx.createGain();
    master.gain.value = muted ? 0 : volume * 0.25;
    master.connect(ctx.destination);
  });

  const apply = () => {
    if (master) master.gain.value = muted ? 0 : volume * 0.25;
  };

  return {
    play(name) {
      const ctx = audioContext();
      if (!ctx || !master || muted) return;
      const now = ctx.currentTime;
      for (const n of PATCHES[name]) {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = n.type ?? 'sine';
        osc.frequency.value = n.f;
        const start = now + n.at;
        g.gain.setValueAtTime(0, start);
        g.gain.linearRampToValueAtTime(n.gain ?? 0.5, start + 0.008);
        g.gain.exponentialRampToValueAtTime(0.0001, start + n.dur);
        osc.connect(g).connect(master);
        osc.start(start);
        osc.stop(start + n.dur + 0.02);
      }
    },
    setVolume(v) {
      volume = Math.max(0, Math.min(1, v));
      apply();
    },
    setMuted(m) {
      muted = m;
      apply();
    },
  };
}
